import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router';

import { getOpenAppWithOTPUrl } from '../../../common/mobile-utils';
import useMixpanel from '../../../hooks/useMixpanel';
import { useLoginService } from '../../../services/loginService';
import { getPortalRuntimeConfig } from '../../../services/runtimeConfig';
import { useSignUpService } from '../../../services/signUpService';
import loginStyles from '../../Login/index.module.scss';
import formStyles from '../../Login/LoginContent/index.module.scss';
import styles from './index.module.scss';
import MobileWelcomeScreen from './MobileWelcomScreen';
import PatientDataContent from './PatientDataContent';
import QRCodeContent from './QRCodeContent';

function WelcomeScreen() {
  const { appLink } = getPortalRuntimeConfig();
  const {
    state: { errorMessage, isWelcomeState, signUpFlowState },
  } = useSignUpService();
  const { hash } = useLocation();
  const urlParams = queryString.parse(hash);
  const idToken = urlParams?.id_token as string;

  const [mobileUrl, setMobileUrl] = useState<string>();
  const { track, trackFail, trackSuccess } = useMixpanel({
    screen: 'post_signup_welcome_screen',
  });

  const updateMobileUrl = async (idToken: string) => {
    const mixpanelAction = 'otp_for_mobile_registration';
    try {
      track(mixpanelAction);
      const result = await loginOTPAction.mutateAsync({ idToken });
      if (result) {
        trackSuccess(mixpanelAction);
        setMobileUrl(getOpenAppWithOTPUrl(result.userUuid, result.otp));
      } else {
        trackFail(mixpanelAction, {
          client_error: 'api succeeded, missing otp result',
        });
        setMobileUrl(appLink);
      }
    } catch (ex) {
      trackFail(mixpanelAction, { ex });
      setMobileUrl(appLink);
    }
  };

  const { state, login, loginOTPAction } = useLoginService({
    async onFinish(idToken) {
      await updateMobileUrl(idToken);
    },
  });

  useEffect(() => {
    if (isWelcomeState || state.isWelcome) {
      if (signUpFlowState?.username && signUpFlowState?.password) {
        login({
          username: signUpFlowState.username,
          password: signUpFlowState.password,
        });
      } else if (idToken || state.idToken) {
        updateMobileUrl(idToken || state.idToken);
      }
    }
  }, [
    signUpFlowState.username,
    signUpFlowState.password,
    signUpFlowState?.state,
    idToken,
    state.idToken,
  ]);

  if (isMobile) {
    return <MobileWelcomeScreen url={mobileUrl} />;
  }

  return (
    <div
      className={classNames(
        loginStyles.boxContainer,
        styles.root,
        formStyles.root,
        'text-base font-normal text-neutrals-n-400 text-left w-full',
        {
          [loginStyles.hide]: !isWelcomeState,
        },
      )}
    >
      <div className={classNames('flex flex-col items-center', styles.header)}>
        <span className={loginStyles.title}>Welcome!</span>
        <div className="flex flex-col items-center justify-center text-center">
          Your account was created successfully.
          <div>
            OneStep offers both a desktop clinical portal and a mobile clinician app, you can always
            access both platforms.
          </div>
        </div>
      </div>
      <div className="flex items-start">
        <QRCodeContent mobileUrl={mobileUrl} />
        <PatientDataContent />
      </div>
      {errorMessage || state.errorMessage ? (
        <div className={formStyles.error}>{errorMessage || state.errorMessage}</div>
      ) : null}
    </div>
  );
}
export default observer(WelcomeScreen);
